import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, OnChanges, OnDestroy } from '@angular/core';
import { MasterService } from 'src/app/services/master.service';
import * as languageLibrary from '../../../../../../services/language'
import { MessageService } from 'src/app/services/message.service';
import { Product } from 'src/app/models/product.model';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { formatCurrency } from 'src/app/utils/format';
import { clean } from 'src/app/utils/numeric';
import { SaleProduct } from 'src/app/models/sale-product.model';
import { ProductSetup } from 'src/app/models/product-setup.model';

@Component({
    selector: 'products',
    templateUrl: './products.component.html',
    styleUrls: ['./products.component.scss'],
    standalone: false
})
export class SalesProductsComponent implements OnInit, OnChanges, OnDestroy {
    
    
    

    // Indicates whether or not the sale is unlocked and can accept changes.
    @Input() isUnLock: boolean = true

    @Input() saleId: number = 0

    protected loanTerm: number = -1

    // The current search text to filter the list by.
    protected inquiry: string | null = null

    protected words = languageLibrary.language

    protected language: string = 'EN'

    protected products: Product[] = [];

    protected selectableProducts: Product[] = []

    /** The raw sale product data we can use to compute initial cost and selling prices with. */
    protected saleProducts: SaleProduct[] = []

    protected form = null

    /**
    * Helper saleProduct to consistently get the list of form saleProducts as an Array.
    */
    get saleProductsList() {
        return this.form.controls['saleProductsList'] as FormArray;
    }

    get purchases() {
        const items = []

        for (let group of this.saleProductsList.controls) {
            const controls = (group as FormGroup).controls
            items.push({
                id: parseInt(controls.id.value),
                contactId: parseInt(controls.contactId.value),
                productId: parseInt(controls.productId.value),
                saleId: parseInt(controls.saleId.value),
                dealerCost: clean(controls.dealerCost.value),
                sellingPrice: clean(controls.sellingPrice.value),
                contactRoleType: controls.contactRoleType.value,
                productName: controls.productName.value,
                contactName: controls.contactName.value
            })
        }

        return items
    }

    /** Compute the total price of all accessories. */
    get total(): number {
        let total = 0
        for (const product of this.saleProductsList.controls) {
            const id = parseInt(product.get('id').value)
            if (!id || (id < 0)) { // only include saved/commited products
                continue
            }

            total += clean(product.get('sellingPrice').value)
        }

        return total
    }

    /** Compute the total cost of all accessories. */
    get cost(): number {
        let cost = 0
        for (const product of this.saleProductsList.controls) {
            const id = parseInt(product.get('id').value)
            if (!id || (id < 0)) { // only include saved/commited accessories
                continue
            }

            cost += clean(product.get('dealerCost').value)
        }

        return cost
    }

    constructor(private master: MasterService, private ms: MessageService, private fb: FormBuilder) {
        this.form = this.fb.group({
            saleProductsList: this.fb.array([])
        })
    }

    ngOnInit() {
        this.fetchProducts()

        this.ms.channelComponents$.subscribe((event) => {
            if (!event || !event.message) {
                return
            }

            switch (event.message) {
                case 'termChanged':
                    this.loanTerm = parseInt(event.value)
                    break
            }
        })
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.isUnLock) {
            this.form.disable({ emitEvent: false })
            if (this.isUnLock) {
                this.form.enable({ emitEvent: false })
            }
        }

        if (changes.saleId) {
            if (!this.saleId || (this.saleId <= 0)) {
                return
            }

            this.getSaleProducts()
        }
    }

    ngOnDestroy() {
    }

    // CORPORATE PRODUCTS
    fetchProducts() {
        let inquiryParam = ''
        if (this.inquiry && (this.inquiry)) {
            inquiryParam = `&q=${encodeURIComponent(this.inquiry)}`
        }

        this.master.get(`corporation/products?${inquiryParam}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.products || (res.data.products.length <= 0)) {
                return
            }

            this.products = res.data.products
            this.getSaleProducts()
        })
    }

    /**
    * Add a new sale products row to the form array
    * @param e Event that triggered this method, if any
    */
    public add = (product): void => {
        const saleProduct = this.fb.group({
            id: [null],
            contactId: [null, [Validators.required]],
            productId: [product.id],
            saleId: [this.saleId, [Validators.required]],
            dealerCost: [null],
            sellingPrice: [null],
            contactRoleType: [product.category.key],
            productName: [product.name],
            contactName: [null]
        })

        this.saleProductsList.push(saleProduct)
        this.determineSelectables()
    }

    public getSaleProducts() {
        if (!this.saleId || isNaN(this.saleId) || (this.saleId <= 0)) {
            return
        }

        this.master.get(`sales/${this.saleId}/products`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.saleProducts || (res.data.saleProducts.length <= 0)) {
                 this.determineSelectables()
                return
            }

            this.assembleProducts(res.data.saleProducts)
            this.determineSelectables()
        })
    }

    public assembleProducts(items: SaleProduct[]) {
        this.saleProducts = items
        this.saleProductsList.clear()

        for (const item of this.saleProducts) {
            if (item.setup && (item.sellingPrice <= 0)) {
                const {cost, price} = this.calcFees(item.setup, item.product.category.key, item.product.name, this.loanTerm)
                item.dealerCost = cost
                item.sellingPrice = price
                this.modifySaleProduct(item)
            }

            this.saleProductsList.push(
                this.fb.group({
                    id: [item.id],
                    contactId: [item.contactId],
                    productId: [item.productId],
                    saleId: [item.saleId, [Validators.required]],
                    dealerCost: [formatCurrency(item.dealerCost)],
                    sellingPrice: [formatCurrency(item.sellingPrice)],
                    contactRoleType: [item.product.category ? item.product.category.key : null],
                    productName: [item.product.name ? item.product.name : null],
                    contactName: [item.contact ? item.contact.name : null]
                })
            )
        }

        this.compute(false)
    }

    /**
     * 
     * @param saleProduct 
     * @param index 
     * @returns 
     */
    public createSaleProduct(saleProduct, index?: number) {
        const payload = JSON.parse(JSON.stringify(saleProduct.value))
        payload.dealerCost = clean(payload.dealerCost)
        payload.sellingPrice = clean(payload.sellingPrice)

        if ((payload.productId === null) && (payload.contactId === null)) { // need at least one of them for the initial save
            return
        }

        this.master.post(`sales/${this.saleId}/products`, { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.product) {
                return
            }

            const item = this.saleProductsList.controls.find((purchase) => purchase.get('productId').value == res.data.product.productId)
            if (!item) {
                return
            }

            const saleProduct: SaleProduct = res.data.product

            item.get('id').setValue(saleProduct.id)
            item.get('contactName').setValue(saleProduct.contact.name)
            
            if (saleProduct && saleProduct.setup) {
                const {cost, price} = this.calcFees(saleProduct.setup, saleProduct.product.category.key, saleProduct.product.name, this.loanTerm)
                saleProduct.dealerCost = cost
                saleProduct.sellingPrice = price

                item.get('dealerCost').setValue(formatCurrency(cost))
                item.get('sellingPrice').setValue(formatCurrency(price))

                this.modifySaleProduct(saleProduct)
                return
            }

            this.compute(false)
        })
    }

    /**
     * Compute the initial dealer cost and price for a product given 
     * the product type and loan term in months.
     * @param {string} key The product category external identit to determine how to compute.
     * @param {string} [name=null] An optional product name to help distinguish it for those products that share the same key.
     * @param {number} [loanTerm=0] The current sale loan term in months.
     * @returns {Object} Associative array with keys cost and price based on any calculations that were performed.
     */
    public calcFees = (product: ProductSetup, key: string, name: string | null = null, loanTerm: number = 0): any => {
        if (!key || (key.length <= 0)) {
            return {cost: 0, price: 0}
        }

        switch (key) {
            case 'warranty':
                return {cost: product.dealerCost, price: product.sellingPrice}
            case 'gap':
                if (product.termRange) {
                    const range = product.termRange.find((item) => ((item.termMonthsFrom >= loanTerm) && (item.termMonthsTo <= loanTerm)))
                    if (range) {
                        return {cost: range.cost, price: range.premium}
                    }
                }
                
                return {cost: 0, price: 0}
            case 'dca':
                if (product.termRange) {
                    const range = product.termRange.find((item) => ((item.termMonthsFrom >= loanTerm) && (item.termMonthsTo <= loanTerm)))
                    if (range) {
                        return {cost: 0, price: range.dollarCap}
                    }
                }

                return {cost: 0, price: 0}
            case 'lifeinsurance':
                if (name && (name.toLocaleLowerCase() === 'accident health')) {
                    return {cost: 0, price: 0}
                }

                return {cost: 0, price: 0}
            default:
                return {cost: 0, price: 0}
        }
    }

    /**
     * 
     * @param saleProduct 
     */
    public modifySaleProduct(saleProduct) {
        const payload = JSON.parse(JSON.stringify(saleProduct))
        payload.dealerCost = clean(payload.dealerCost)
        payload.sellingPrice = clean(payload.sellingPrice)
        delete(payload.id)

        this.master.put(`sales/${this.saleId}/products/${saleProduct.id}`, { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.compute(false)
        })
    }

    public discardProduct(index) {
        if (this.saleProductsList.controls[index].get("id").value == null) {
            this.saleProductsList.removeAt(index)
            return
        }

        this.master.discard(`sales/${this.saleId}/products/${this.saleProductsList.controls[index].get("id").value}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.saleProducts && res.data.saleProducts.length == 0) {
                return
            }

            this.saleProductsList.removeAt(index)
            this.compute(true)
            this.determineSelectables()
        })
    }

    private compute(save: boolean) {
        const items = JSON.parse(JSON.stringify(this.purchases))
        this.ms.channelComponents$.emit({message: 'productsChanged', section: 'products', value: {value: this.total, save: save, productCount: items.length, products: items}})
    }

    /**
     * A product, at the given index, provider (contact) was selected by the user so lets save it.
     * @param contactId 
     * @param index
     */
    protected setContactIdEvent(contactId, index) {
        if (!contactId) {
            return
        }

        if (this.saleProductsList.controls[index].get("contactId").value == contactId) {
            return
        }

        this.saleProductsList.controls[index].get("contactId").setValue(contactId)
        this.saleProductsList.controls[index].markAsDirty();
        this.changed(contactId, index)
    }

    /**
    * Handle input blur or change events 
    * and recalculate when needed.
    * @param {Event} e Event meta data related to the change.
    * @param {number} index The index of the sale product that changed.
    */
    protected changed(e: Event, index): void {
        if ((index < 0) || (index >= this.saleProductsList.controls.length)) {
            return
        }
        
        if (this.saleProductsList.controls[index].invalid) {
            return
        }

        if (this.saleProductsList.controls[index].pristine) {
            return
        }

        const id = parseInt(this.saleProductsList.controls[index].get('id').value)

        if (id && (!isNaN(id)) && (id > 0)) {
            this.modifySaleProduct(this.saleProductsList.controls[index].value)
            this.saleProductsList.markAsPristine()
            return
        }

        this.createSaleProduct(this.saleProductsList.controls[index], index)
        this.saleProductsList.markAsPristine()
    }

    /**
     * Based on what is purchased and what is available return a list
     * of additional corporate product selections availabel to the user.
     */
    private determineSelectables = (): void => {
        if (!this.products || (this.products.length <= 0)) {
            this.selectableProducts = []
            return
        }
        
        const purchased: string[] = []
        for (const purchase of this.purchases) {
            if (purchase.contactRoleType === 'warranty') {
                purchased.push(`${purchase.contactRoleType}-${purchase.productName.toLocaleLowerCase().replace(/\s+/g, '-')}`)
                continue
            }

            purchased.push(purchase.contactRoleType)
        }

        if (!purchased || (purchased.length <= 0)) {
            this.selectableProducts = this.products
            return
        }

        this.selectableProducts = this.products.filter((product) => {
            let key = product.category.key
            if (key === 'warranty') {
                key = `${key}-${product.name.toLocaleLowerCase().replace(/\s+/g, '-')}`
            }

            if (purchased.includes(key)) {
                return false
            }

            if ((key === 'gap') && (purchased.includes('debtCancellation'))) {
                return false
            }

            if ((key === 'debtCancellation') && (purchased.includes('gap'))) {
                return false
            }

            return true
        })
    }

}
